import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, LucideIcon } from "lucide-react";

interface Feature {
  text: string;
  dotColor: string;
}

interface TechCardProps {
  title: string;
  description: string;
  badge: {
    text: string;
    variant: "secondary" | "outline";
  };
  icon: {
    Icon: LucideIcon;
    bgColor: string;
    iconColor: string;
  };
  features: Feature[];
  button: {
    text: string;
    href: string;
    disabled?: boolean;
    variant?: "default" | "outline";
  };
  className?: string;
}

export function TechCard({
  title,
  description,
  badge,
  icon,
  features,
  button,
  className = "",
}: TechCardProps) {
  const { Icon } = icon;

  return (
    <Card className={`hover:shadow-lg transition-all duration-300 hover:scale-105 group flex flex-col h-full ${className}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3 mb-2">
          <div className={`p-2 rounded-lg ${icon.bgColor} group-hover:opacity-80 transition-colors`}>
            <Icon className={`h-6 w-6 ${icon.iconColor}`} />
          </div>
          <Badge variant={badge.variant} className="text-xs">
            {badge.text}
          </Badge>
        </div>
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription className="text-sm leading-relaxed">
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col">
        <div className="space-y-3 mb-6 flex-1">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className={`w-2 h-2 rounded-full ${feature.dotColor}`}></div>
              {feature.text}
            </div>
          ))}
        </div>
        <Button
          className="w-full"
          variant={button.variant || "default"}
          disabled={button.disabled}
          asChild
        >
          <Link
            href={button.href}
            className="flex items-center justify-center gap-2"
          >
            {button.text}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}